import _ from "lodash";
import { translate } from "../../../services/Translations/translatorService";
import { Person } from "../../../types/Person";
import { getEditorHtml } from "../../../services/Editor/editorFunctions";
import { BackofficeUser } from "../../../types/BackofficeUser";
import { Editor } from "@tiptap/react";
import { CasePdfGeneratorFormValues } from "../../../types/CasePdfGenerator";
import { CasePerson } from "../../../types/CasePerson";
import { ProductUnion } from "../../../types/ProductUnion";
import dayjs from "dayjs";

const buildAddress = (values: Record<string, any>) => {
  // Fixed recipients are resolved from current case data by the PDF backend.
  if (values.recipientType !== "custom") {
    return null;
  }

  const baseData = {
    firm: null,
    gender: null,
    name: null,
    department: null,
    subDepartment: null,
    streetAddress: null,
    addressAddition: null,
    postalCode: null,
    addressLocality: null,
    postOfficeBoxNumber: null,
    postOfficeBoxPostalCode: null,
    postOfficeBoxAddressLocality: null,
  };

  return _.merge({}, baseData, values.address);
};

const getExternalReference = (product: ProductUnion, switchValue: string) => {
  if (switchValue.startsWith("processParticipantsPeople")) {
    return product.processParticipantsPeople.find(
      (participant: Person) => participant.id + "" === switchValue.split(".")[1],
    ).casePerson.reference;
  }

  switch (product.productClassName) {
    case "traffic":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "authority":
          return product.fineNoticeReference;
        case "prosecution":
          return product.prosecutionReference;
        default:
          return null;
      }
    case "settlement":
    case "divorce":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "opposingCounsel":
          return product.referenceOpponentLawyer;
        default:
          return null;
      }
    case "trafficAccident":
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "dataLeakContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
    case "employmentContract":
      switch (switchValue) {
        case "debtCollectionAgency":
          return product.debtCollectionAgencyReference;
        case "court":
          return product.courtReference;
        case "opposingCounsel":
          return product.referenceOpponentLawyer;
        case "contractNumber":
          return product.contractNumber;
        case "prosecution":
          return product.prosecutionReference;
        case "authority":
          return product.authorityReference;
        case "opposingPartyReference":
          return product.opposingPartyReference;
        default:
          return null;
      }
    case "generalCivilLaw":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "opposingCounsel":
          return product.referenceOpponentLawyer;
        case "policeReportReferenceNumber":
          return product.policeReportReferenceNumber;
        case "prosecution":
          return product.prosecutionReference;
        case "opposingPartyReference":
          return product.opposingPartyReference;
        default:
          return null;
      }
    case "civilServiceLaw":
      switch (switchValue) {
        case "court":
          return product.courtReference;
        case "opposingCounsel":
          return product.referenceOpponentLawyer;
        case "policeReportReferenceNumber":
          return product.policeReportReferenceNumber;
        case "prosecution":
          return product.prosecutionReference;
        case "authority":
          return product.authorityReference;
        default:
          return null;
      }
    case "rentalContract":
      switch (switchValue) {
        case "debtCollectionAgency":
          return product.debtCollectionAgencyReference;
        case "propertyManager":
          return product.propertyManagerReference;
        case "opposingCounsel":
          return product.referenceOpponentLawyer;
        case "court":
          return product.courtReference;
        case "prosecution":
          return product.prosecutionReference;
        case "opposingPartyReference":
          return product.opposingPartyReference;
        default:
          return null;
      }
    default:
      return null;
  }
};

const getExternalReferenceSelectValue = (product: ProductUnion, recipientType: string) => {
  const defaultFieldName = "free";

  if (recipientType.startsWith("processParticipantsPeople")) {
    const index = recipientType.split(".")[1];
    return "processParticipantsPeopleReference." + index;
  }

  switch (product.productClassName) {
    case "traffic":
      switch (recipientType) {
        case "authority":
          return product.fineNoticeReference ? "authority" : defaultFieldName;
      }
    // fallthrough
    case "rentalContract":
      switch (recipientType) {
        case "propertyManager":
          return product.propertyManagerReference ? "propertyManager" : defaultFieldName;
      }
    // fallthrough
    default:
      switch (recipientType) {
        case "court":
          return product.courtReference ? "court" : defaultFieldName;
        case "contractualPartnerLegalEntity":
        case "opposingParty":
          if (product.opposingPartyReference) {
            return "opposingPartyReference";
          } else if (product.contractNumber) {
            return "contractNumber";
          }

          return defaultFieldName;
        case "opposingCounsel":
          return product.referenceOpponentLawyer ? "opposingCounsel" : defaultFieldName;
        case "debtCollectionAgency":
          return product.debtCollectionAgencyReference ? "debtCollectionAgency" : defaultFieldName;
        case "authority":
          return product.authorityReference ? "authority" : defaultFieldName;
        case "prosecution":
          return product.prosecutionReference ? "prosecution" : defaultFieldName;
        case "custom":
        case "insurance":
        case "opposingCarLiabilityInsurance":
        case "opposingLiabilityInsurance":
        case "petitioner":
        case "advoAssistRepresentative":
        default:
          return defaultFieldName;
      }
  }
};

const getReferenceOptions = (product: ProductUnion) => {
  let references = [];

  switch (product.productClassName) {
    case "settlement":
    case "divorce":
      references.push(
        {
          type: "court",
          label: "Aktenzeichen Gericht",
          isHidden: !product.courtReference,
        },
        {
          type: "opposingCounsel",
          label: "Aktenzeichen Anwalt Gegenseite",
          isHidden: !product.referenceOpponentLawyer,
        },
        ...getProcessParticipantsPeopleReferenceOptions(product),
      );
      break;
    case "traffic":
      references.push(
        {
          type: "court",
          label: "Aktenzeichen Gericht",
          isHidden: !product.courtReference,
        },
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.fineNoticeReference,
        },
        {
          type: "prosecution",
          label: "Aktenzeichen Staatsanwaltschaft",
          isHidden: !product.prosecutionReference,
        },
        ...getProcessParticipantsPeopleReferenceOptions(product),
      );
      break;
    case "trafficAccident":
      references.push(
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.authorityReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product),
      );
      break;
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "dataLeakContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
    case "employmentContract":
      references.push(
        {
          type: "debtCollectionAgency",
          label: "Aktenzeichen Inkasso",
          isHidden: !product.debtCollectionAgencyReference,
        },
        {
          type: "contractNumber",
          label: "Vertragsnummer",
          isHidden: !product.contractNumber,
        },
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.authorityReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product),
      );
      break;
    case "generalCivilLaw":
      references.push(
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product),
      );
      break;
    case "civilServiceLaw":
      references.push(
        {
          type: "authority",
          label: "Aktenzeichen Behörde",
          isHidden: !product.authorityReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product),
      );
      break;
    case "rentalContract":
      references.push(
        {
          type: "propertyManager",
          label: "Aktenzeichen Hausverwaltung",
          isHidden: !product.propertyManagerReference,
        },
        ...getLitigationReferenceOptions(product),
        ...getProcessParticipantsPeopleReferenceOptions(product),
      );
      break;
    default:
      break;
  }

  return references;
};

const getLitigationReferenceOptions = (product: ProductUnion) => {
  return [
    {
      type: "opposingCounsel",
      label: "Aktenzeichen Anwalt Gegenseite",
      isHidden: !product.referenceOpponentLawyer,
    },
    {
      type: "court",
      label: "Aktenzeichen Gericht",
      isHidden: !product.courtReference,
    },
    {
      type: "policeReportReferenceNumber",
      label: "Aktenzeichen Polizei",
      isHidden: !product.policeReportReferenceNumber,
    },
    {
      type: "prosecution",
      label: "Aktenzeichen Staatsanwaltschaft",
      isHidden: !product.prosecutionReference,
    },
    {
      type: "opposingPartyReference",
      label: "Internes Aktenzeichen Gegenseite",
      isHidden: !product.opposingPartyReference,
    },
  ];
};

function hasCasePersonReference(p: Person): p is Person & { casePerson: CasePerson } {
  return Boolean(p.casePerson?.reference);
}

const getProcessParticipantsPeopleReferenceOptions = (product: ProductUnion) => {
  //cast won't be needed anymore as soon as the product types are correctly implemented
  return (product.processParticipantsPeople as Person[]).filter(hasCasePersonReference).map((person) => {
    return {
      type: "processParticipantsPeopleReference." + person.id,
      label: "Aktenzeichen " + translate("participant.labelType.values." + person.labelType),
      isHidden: false,
    };
  });
};

const getCasePdfFormParams = ({ values, editor, currentUser }: casePdfFormParamsProps) => {
  return {
    ...values,
    content: getEditorHtml(editor),
    address: buildAddress(values),
    externalReference: values.externalReference?.trim() ? values.externalReference : null,
    senderMedium: values.senderMedium === "none" ? null : values.senderMedium,
    externalLawyer: currentUser.isExternal ? currentUser : null,
    insurancePolicyNumber: ["insurance", "opposingCarLiabilityInsurance", "opposingLiabilityInsurance"].includes(
      values.recipientType,
    )
      ? values.insurancePolicyNumber
      : null,
    caseReferenceNumber: ["insurance", "opposingCarLiabilityInsurance", "opposingLiabilityInsurance"].includes(
      values.recipientType,
    )
      ? values.caseReferenceNumber
      : null,
  };
};

const getDocumentName = (recipientType: string) => {
  const date = dayjs().format("YYYYMMDD");
  const documentName = "_Schreiben";
  let documentSuffix = "";

  switch (recipientType) {
    case "insurance":
      documentSuffix = "_aRSV";
      break;
    case "opposingCarLiabilityInsurance":
      documentSuffix = "_aGgs";
      break;
    case "opposingLiabilityInsurance":
      documentSuffix = "_aHPVGs";
      break;
    case "court":
      documentSuffix = "_aG";
      break;
    case "contractualPartnerLegalEntity":
    case "opposingParty":
      documentSuffix = "_aGs";
      break;
    case "petitioner":
      documentSuffix = "_aM";
      break;
    case "advoAssistRepresentative":
      documentSuffix = "_aTV";
      break;
    case "opposingCounsel":
      documentSuffix = "_agRA";
      break;
    case "debtCollectionAgency":
      documentSuffix = "_aI";
      break;
    case "propertyManager":
      documentSuffix = "_aHv";
      break;
    case "authority":
      documentSuffix = "_aB";
      break;
    case "prosecution":
      documentSuffix = "_aSta";
      break;
    default:
      break;
  }

  return date + documentName + documentSuffix;
};

const getSenderMediumValue = (recipientType: string) => {
  switch (recipientType) {
    case "court":
      return "bea";
    default:
      return "none";
  }
};

type casePdfFormParamsProps = {
  values: Omit<CasePdfGeneratorFormValues, "content">;
  editor: Editor | null;
  currentUser: BackofficeUser;
};

export {
  buildAddress,
  getExternalReference,
  getExternalReferenceSelectValue,
  getSenderMediumValue,
  getReferenceOptions,
  getDocumentName,
  getCasePdfFormParams,
};
